import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Scale, FileText, Users } from 'lucide-react';
import { ProgressDashboard } from '../components/dashboard/ProgressDashboard';
import { CompareModal } from '../components/compare/CompareModal';
import { patchNotesApi, teamApi } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { cn, formatDate } from '../lib/utils';

export default function DashboardPage() {
    const { t } = useLanguage();
    const [compareOpen, setCompareOpen] = useState(false);
    const [patchNotes, setPatchNotes] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [notesRes, teamRes] = await Promise.all([
                patchNotesApi.getAll(),
                teamApi.getAll(),
            ]);
            setPatchNotes(notesRes.data || []);
            setTeamMembers(teamRes.data || []);
        } catch (error) {
            console.error('Failed to load data:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-cyan-500/10">
                        <LayoutDashboard className="text-cyan-400" size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold font-['Rajdhani'] tracking-wider text-white dark:text-white light:text-gray-900">
                            {t('dashboard')}
                        </h1>
                        <p className="text-gray-400 dark:text-gray-400 light:text-gray-600 text-sm sm:text-base">
                            {t('yourProgress')}
                        </p>
                    </div>
                </div>
                <Button
                    onClick={() => setCompareOpen(true)}
                    className="bg-white/5 hover:bg-white/10 text-white dark:text-white light:text-gray-900 border border-white/10 dark:border-white/10 light:border-gray-200 w-full sm:w-auto"
                    data-testid="open-compare-button"
                >
                    <Scale size={16} className="mr-2" />
                    {t('compareItems')}
                </Button>
            </div>

            {/* Progress Dashboard */}
            <ProgressDashboard />

            {/* Patch Notes Section */}
            <section className="space-y-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/10">
                        <FileText className="text-amber-400" size={24} />
                    </div>
                    <h2 className="text-2xl font-['Rajdhani'] font-bold text-white dark:text-white light:text-gray-900">
                        {t('patchNotes')}
                    </h2>
                </div>

                {loading ? (
                    <div className="space-y-4">
                        {[1, 2].map((i) => (
                            <div key={i} className="p-6 rounded-xl bg-card dark:bg-card light:bg-white border border-white/5 dark:border-white/5 light:border-gray-200 animate-pulse">
                                <div className="h-6 bg-white/5 dark:bg-white/5 light:bg-gray-100 rounded w-1/3 mb-3" />
                                <div className="h-4 bg-white/5 dark:bg-white/5 light:bg-gray-100 rounded w-full mb-2" />
                                <div className="h-4 bg-white/5 dark:bg-white/5 light:bg-gray-100 rounded w-2/3" />
                            </div>
                        ))}
                    </div>
                ) : patchNotes.length === 0 ? (
                    <div className="p-8 rounded-xl bg-card dark:bg-card light:bg-white border border-white/5 dark:border-white/5 light:border-gray-200 text-center">
                        <p className="text-gray-500">{t('noPatchNotes')}</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {patchNotes.slice(0, 3).map((note, index) => (
                            <div
                                key={note.id}
                                data-testid={`patch-note-${note.id}`}
                                className={cn(
                                    "p-6 rounded-xl bg-card dark:bg-card light:bg-white border border-white/5 dark:border-white/5 light:border-gray-200 hover:border-amber-500/30 transition-colors",
                                    "animate-fade-in opacity-0",
                                    `stagger-${index + 1}`
                                )}
                            >
                                <div className="flex items-start justify-between gap-4 mb-3">
                                    <div>
                                        <h3 className="font-['Rajdhani'] font-semibold text-xl text-white dark:text-white light:text-gray-900">
                                            {note.title}
                                        </h3>
                                        <span className="inline-block px-2 py-0.5 rounded text-xs font-mono bg-amber-500/10 text-amber-400 border border-amber-500/20">
                                            v{note.version}
                                        </span>
                                    </div>
                                    <span className="text-xs text-gray-500 font-mono shrink-0">
                                        {note.date || formatDate(note.created_at)}
                                    </span>
                                </div>
                                <p className="text-gray-400 dark:text-gray-400 light:text-gray-600 whitespace-pre-line">
                                    {note.content}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Team Section */}
            <section className="space-y-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-500/10">
                        <Users className="text-purple-400" size={24} />
                    </div>
                    <h2 className="text-2xl font-['Rajdhani'] font-bold text-white dark:text-white light:text-gray-900">
                        {t('ourTeam')}
                    </h2>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="p-6 rounded-xl bg-card dark:bg-card light:bg-white border border-white/5 dark:border-white/5 light:border-gray-200 animate-pulse text-center">
                                <div className="w-20 h-20 rounded-full bg-white/5 dark:bg-white/5 light:bg-gray-100 mx-auto mb-4" />
                                <div className="h-5 bg-white/5 dark:bg-white/5 light:bg-gray-100 rounded w-2/3 mx-auto mb-2" />
                                <div className="h-4 bg-white/5 dark:bg-white/5 light:bg-gray-100 rounded w-1/2 mx-auto" />
                            </div>
                        ))}
                    </div>
                ) : teamMembers.length === 0 ? (
                    <div className="p-8 rounded-xl bg-card dark:bg-card light:bg-white border border-white/5 dark:border-white/5 light:border-gray-200 text-center">
                        <p className="text-gray-500">{t('noTeamMembers')}</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {teamMembers.map((member, index) => (
                            <div
                                key={member.id}
                                data-testid={`team-member-${member.id}`}
                                className={cn(
                                    "p-6 rounded-xl bg-card dark:bg-card light:bg-white border border-white/5 dark:border-white/5 light:border-gray-200 text-center",
                                    "hover:border-purple-500/30 transition-colors group",
                                    "animate-fade-in opacity-0",
                                    `stagger-${index + 1}`
                                )}
                            >
                                {/* Avatar */}
                                <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden border-2 border-white/10 group-hover:border-purple-500/50 transition-colors">
                                    {member.avatar_url ? (
                                        <img
                                            src={member.avatar_url}
                                            alt={member.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-purple-500/20 flex items-center justify-center">
                                            <span className="text-2xl font-bold text-purple-400">
                                                {member.name?.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Name & Alias & Role */}
                                <h3 className="font-['Rajdhani'] font-semibold text-lg text-white dark:text-white light:text-gray-900">
                                    {member.name}
                                </h3>
                                {member.alias && (
                                    <p className="text-xs text-white/60 dark:text-white/60 light:text-gray-500 -mt-0.5 mb-1">
                                        {member.alias}
                                    </p>
                                )}
                                <p className="text-sm text-gray-500 dark:text-gray-500 light:text-gray-400 mb-4">
                                    {member.role}
                                </p>

                                {/* Social Links */}
                                {member.social_links && (
                                    <div className="flex items-center justify-center gap-3">
                                        {member.social_links.twitter && (
                                            <a
                                                href={member.social_links.twitter}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-2 rounded-lg bg-white/5 dark:bg-white/5 light:bg-gray-100 text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                                </svg>
                                            </a>
                                        )}
                                        {member.social_links.discord && (
                                            <a
                                                href={member.social_links.discord}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-2 rounded-lg bg-white/5 dark:bg-white/5 light:bg-gray-100 text-gray-400 hover:text-[#5865F2] hover:bg-[#5865F2]/10 transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                                                </svg>
                                            </a>
                                        )}
                                        {member.social_links.twitch && (
                                            <a
                                                href={member.social_links.twitch}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-2 rounded-lg bg-white/5 dark:bg-white/5 light:bg-gray-100 text-gray-400 hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                                                </svg>
                                            </a>
                                        )}
                                        {member.social_links.youtube && (
                                            <a
                                                href={member.social_links.youtube}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-2 rounded-lg bg-white/5 dark:bg-white/5 light:bg-gray-100 text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                                </svg>
                                            </a>
                                        )}
                                        {member.social_links.roblox && (
                                            <a
                                                href={member.social_links.roblox}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-2 rounded-lg bg-white/5 dark:bg-white/5 light:bg-gray-100 text-gray-400 hover:text-green-400 hover:bg-green-500/10 transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M5.164 0L.16 18.928 18.836 24l5.004-18.928L5.164 0zm7.906 15.263l-4.358-1.2 1.2-4.357 4.358 1.2-1.2 4.357z"/>
                                                </svg>
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Compare Modal */}
            <CompareModal open={compareOpen} onOpenChange={setCompareOpen} />
        </div>
    );
}
